import { RemoveButton, Section } from '@components'
import AddIcon from '@mui/icons-material/Add'
import { Button, Divider } from '@mui/material'
import Box from '@mui/material/Box'
import { type FC, useCallback } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { FormValues } from '../Calculator'
import { defaultFormValues } from '../helpers/config'
import FieldGroup from './FieldGroup'
import { HighlightedContainer } from './styles'

interface FormSectionProps {
	section: keyof FormValues
}

const FormSection: FC<FormSectionProps> = ({ section }: FormSectionProps) => {
	const { t } = useTranslation()

	const { control } = useFormContext()

	const { fields, append, remove } = useFieldArray({
		control,
		name: section,
	})

	const handleAddNewRow = useCallback((): void => {
		append({ ...defaultFormValues[section] })
	}, [append, section])

	return (
		<Box mt={2}>
			<Section
				title={t('calculator.sections.title', { context: section }) ?? section}
				subtitle={t('calculator.sections.subtitle', { context: section })}
			>
				<Box>
					{fields.map((field, index) => (
						<Box key={index}>
							{index > 0 && <Divider sx={{ my: 1 }} />}
							<HighlightedContainer>
								<FieldGroup field={field} section={section} index={index} />

								<Box>
									<RemoveButton
										label={t('button.remove')}
										onRemove={remove}
										value={index}
									/>
								</Box>
							</HighlightedContainer>
						</Box>
					))}
				</Box>

				<Box mt={4}>
					<Button variant="outlined" onClick={handleAddNewRow}>
						<AddIcon />
						{t('button.add')}
					</Button>
				</Box>
			</Section>
		</Box>
	)
}

export { FormSection }
